/**
 * Admin client — wraps apiFetch with all admin endpoint methods.
 * Groups by resource: templates, tenant-templates, roles, role-members,
 * role-rules, grants, audit-log.
 *
 * §9.1-§9.9 + §10 of 02_rest_api.md
 */

import { apiFetch } from '@/lib/api/client.js';
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

const PaginationSchema = z.object({
  page_number: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  total_items: z.number(),
});

export type Pagination = z.infer<typeof PaginationSchema>;

// ---------------------------------------------------------------------------
// Templates (§9.1 global catalog — super_admin required)
// ---------------------------------------------------------------------------

const TemplateItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']),
  category: z.string(),
  title: z.record(z.string()).nullable().optional(),
  status: z.enum(['active', 'deprecated']),
  published_at: z.string(),
  published_by: z.string().nullable().optional(),
  deprecation_reason: z.string().nullable().optional(),
  changelog: z.string().nullable().optional(),
  installed_in_tenants_count: z.number().optional(),
});

const TemplateDetailSchema = z.object({
  id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']),
  category: z.string(),
  definition: z.record(z.unknown()),
  status: z.enum(['active', 'deprecated']),
  published_at: z.string(),
  published_by: z.string().nullable().optional(),
  deprecated_at: z.string().nullable().optional(),
  deprecation_reason: z.string().nullable().optional(),
  changelog: z.string().nullable().optional(),
});

const TemplateListResponseSchema = z.object({
  items: z.array(TemplateItemSchema),
  pagination: PaginationSchema,
});

const TemplateValidateResponseSchema = z.object({
  ok: z.boolean(),
  issues: z.array(z.record(z.unknown())),
});

export type TemplateItem = z.infer<typeof TemplateItemSchema>;
export type TemplateDetail = z.infer<typeof TemplateDetailSchema>;
export type TemplateListResponse = z.infer<typeof TemplateListResponseSchema>;
export type TemplateValidateResponse = z.infer<typeof TemplateValidateResponseSchema>;

export interface TemplateListParams {
  code?: string;
  kind?: 'report' | 'dashboard';
  category?: string;
  version_status?: 'latest' | 'deprecated' | 'all';
  'page-number'?: number;
  'page-size'?: number;
}

export interface CreateTemplateBody {
  code: string;
  version?: number;
  kind: 'report' | 'dashboard';
  category: string;
  definition: Record<string, unknown>;
  changelog?: string;
  published_by?: string;
}

export interface DeprecateTemplateBody {
  reason?: string;
}

// ---------------------------------------------------------------------------
// Tenant templates (§9.2 — admin required)
// ---------------------------------------------------------------------------

const TenantReportDetailSchema = z.object({
  tenant_report_id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']).optional(),
  category: z.string().optional(),
  installed_at: z.string().optional(),
  enabled: z.boolean().optional(),
});

const TenantReportItemSchema = z.object({
  tenant_report_id: z.string(),
  code: z.string(),
  version: z.number(),
  kind: z.enum(['report', 'dashboard']),
  category: z.string(),
  enabled: z.boolean(),
  installed_at: z.string(),
  overrides: z.record(z.unknown()).optional(),
});

const TenantReportListResponseSchema = z.object({
  items: z.array(TenantReportItemSchema),
  pagination: PaginationSchema,
});

export type TenantReportDetail = z.infer<typeof TenantReportDetailSchema>;
export type TenantReportItem = z.infer<typeof TenantReportItemSchema>;
export type TenantReportListResponse = z.infer<typeof TenantReportListResponseSchema>;

export interface TenantTemplateListParams {
  'page-number'?: number;
  'page-size'?: number;
}

export interface InstallTenantTemplateBody {
  template_code: string;
  version?: number;
  overrides?: Record<string, unknown>;
}

export interface MigrateTenantTemplateBody {
  target_version: number;
}

export interface ToggleEnabledBody {
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Roles (§9.6 — admin required)
// ---------------------------------------------------------------------------

const RoleItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  is_default: z.boolean(),
  icon: z.object({ label: z.string(), gradient: z.array(z.string()) }),
  settings: z.record(z.unknown()).optional(),
  members_count: z.number().optional(),
  created_at: z.string(),
});

const RoleDetailSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  is_default: z.boolean(),
  icon: z.object({ label: z.string(), gradient: z.array(z.string()) }),
  settings: z.record(z.unknown()).optional(),
  stats: z
    .object({
      members_count: z.number(),
      grants_count: z.number(),
      rules_count: z.number(),
    })
    .optional(),
  created_at: z.string(),
  created_by: z.string().nullable().optional(),
  updated_at: z.string().optional(),
});

const RoleListResponseSchema = z.object({
  items: z.array(RoleItemSchema),
  pagination: PaginationSchema,
});

export type RoleItem = z.infer<typeof RoleItemSchema>;
export type RoleDetail = z.infer<typeof RoleDetailSchema>;
export type RoleListResponse = z.infer<typeof RoleListResponseSchema>;

export interface RoleListParams {
  'page-number'?: number;
  'page-size'?: number;
}

export interface CreateRoleBody {
  code: string;
  name: string;
  description?: string;
  is_default?: boolean;
  icon_label: string;
  icon_gradient: [string, string];
  settings?: Record<string, unknown>;
}

export interface UpdateRoleBody {
  name?: string;
  description?: string;
  is_default?: boolean;
  icon_label?: string;
  icon_gradient?: [string, string];
  settings?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Role members (§9.7)
// ---------------------------------------------------------------------------

const MemberItemSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  user_email: z.string(),
  user_display_name: z.string().nullable().optional(),
  source: z.enum(['manual', 'rule']),
  rule_id: z.string().nullable().optional(),
  added_at: z.string(),
  added_by: z.string().nullable().optional(),
});

const MemberListResponseSchema = z.object({
  items: z.array(MemberItemSchema),
  pagination: PaginationSchema,
});

const BatchResultSchema = z.object({
  added: z.number(),
  removed: z.number(),
  skipped: z.array(z.object({ user_id: z.string(), reason: z.string() })),
});

export type MemberItem = z.infer<typeof MemberItemSchema>;
export type MemberListResponse = z.infer<typeof MemberListResponseSchema>;
export type BatchResult = z.infer<typeof BatchResultSchema>;

export interface MemberListParams {
  'page-number'?: number;
  'page-size'?: number;
}

export interface AddMemberBody {
  user_id: string;
  source?: 'manual';
}

export interface BatchMembersBody {
  add?: string[];
  remove?: string[];
}

// ---------------------------------------------------------------------------
// Role rules (§9.8)
// ---------------------------------------------------------------------------

const RuleConditionSchema = z.object({
  field: z.string(),
  op: z.enum(['eq', 'neq', 'in', 'starts_with', 'ends_with', 'contains']),
  value: z.unknown(),
});

const RuleItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  condition: RuleConditionSchema,
  matched_count: z.number().optional(),
  last_evaluated_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string().optional(),
});

const RuleListResponseSchema = z.object({
  items: z.array(RuleItemSchema),
  pagination: PaginationSchema,
});

const RuleTestResponseSchema = z.object({
  matches: z.boolean(),
});

const RuleApplyResponseSchema = z.object({
  added: z.number(),
  removed: z.number(),
  errors: z.array(z.object({ user_id: z.string(), error: z.string() })),
});

export type RuleCondition = z.infer<typeof RuleConditionSchema>;
export type RuleItem = z.infer<typeof RuleItemSchema>;
export type RuleListResponse = z.infer<typeof RuleListResponseSchema>;
export type RuleTestResponse = z.infer<typeof RuleTestResponseSchema>;
export type RuleApplyResponse = z.infer<typeof RuleApplyResponseSchema>;

export interface RuleListParams {
  'page-number'?: number;
  'page-size'?: number;
}

export interface CreateRuleBody {
  name: string;
  condition: RuleCondition;
  enabled?: boolean;
}

export interface UpdateRuleBody {
  name?: string;
  condition?: RuleCondition;
  enabled?: boolean;
}

export interface TestRuleBodyInline {
  rule: RuleCondition;
  user_attributes: Record<string, unknown>;
}

export interface TestRuleBodyByRef {
  rule_id: string;
  user_id: string;
}

export type TestRuleBody = TestRuleBodyInline | TestRuleBodyByRef;

// ---------------------------------------------------------------------------
// Grants (§9.9)
// ---------------------------------------------------------------------------

const DataScopeClauseSchema = z.object({
  field: z.string(),
  op: z.enum(['eq', 'in', 'neq', 'starts_with', 'ends_with', 'contains']),
  value: z.unknown(),
});

const GrantItemSchema = z.object({
  id: z.string(),
  tenant_report_id: z.string(),
  subject_type: z.enum(['role', 'user']),
  subject_id: z.string(),
  level: z.enum(['view', 'execute', 'full']),
  data_scope: z.array(DataScopeClauseSchema).optional(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().nullable().optional(),
  updated_by: z.string().nullable().optional(),
});

const GrantListResponseSchema = z.object({
  items: z.array(GrantItemSchema),
  pagination: PaginationSchema,
});

const GrantsMatrixResponseSchema = z.object({
  applied: z.number(),
});

export type DataScopeClause = z.infer<typeof DataScopeClauseSchema>;
export type GrantItem = z.infer<typeof GrantItemSchema>;
export type GrantListResponse = z.infer<typeof GrantListResponseSchema>;
export type GrantsMatrixResponse = z.infer<typeof GrantsMatrixResponseSchema>;

export interface GrantListParams {
  tenant_report_id?: string;
  subject_type?: 'role' | 'user';
  subject_id?: string;
  'page-number'?: number;
  'page-size'?: number;
}

export interface UpsertGrantBody {
  tenant_report_id: string;
  subject_type: 'role' | 'user';
  subject_id: string;
  level: 'view' | 'execute' | 'full';
  data_scope?: DataScopeClause[];
}

export interface GrantMatrixChange {
  subject_type: 'role' | 'user';
  subject_id: string;
  level: 'view' | 'execute' | 'full' | null;
}

export interface GrantsMatrixBody {
  tenant_report_id: string;
  changes: GrantMatrixChange[];
}

// ---------------------------------------------------------------------------
// Audit log (§10)
// ---------------------------------------------------------------------------

const AuditLogItemSchema = z.object({
  id: z.string(),
  actor_user_id: z.string().nullable().optional(),
  actor_email_snapshot: z.string(),
  actor_type: z.string(),
  action: z.string(),
  resource_type: z.string(),
  resource_id: z.string().nullable().optional(),
  before: z.record(z.unknown()).nullable().optional(),
  after: z.record(z.unknown()).nullable().optional(),
  context: z.record(z.unknown()).nullable().optional(),
  occurred_at: z.string(),
});

const AuditLogResponseSchema = z.object({
  items: z.array(AuditLogItemSchema),
  pagination: PaginationSchema,
});

export type AuditLogItem = z.infer<typeof AuditLogItemSchema>;
export type AuditLogResponse = z.infer<typeof AuditLogResponseSchema>;

export interface AuditLogParams {
  from?: string;
  to?: string;
  actor_user_id?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  'page-number'?: number;
  'page-size'?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      q.set(key, String(value));
    }
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

// ---------------------------------------------------------------------------
// Admin client
// ---------------------------------------------------------------------------

export const adminClient = {
  // --- Templates global (super_admin) ---

  listTemplates(params: TemplateListParams = {}): Promise<TemplateListResponse> {
    return apiFetch<TemplateListResponse>(
      `/admin/templates${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  getTemplate(templateId: string): Promise<TemplateDetail> {
    return apiFetch<TemplateDetail>(`/admin/templates/${templateId}`);
  },

  createTemplate(body: CreateTemplateBody): Promise<TemplateDetail> {
    return apiFetch<TemplateDetail>('/admin/templates', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  deprecateTemplate(templateId: string, body: DeprecateTemplateBody = {}): Promise<TemplateDetail> {
    return apiFetch<TemplateDetail>(`/admin/templates/${templateId}/deprecate`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  validateTemplate(body: Record<string, unknown>): Promise<TemplateValidateResponse> {
    // B-1: uses POST /admin/templates/validate (no ID — candidate validation endpoint)
    return apiFetch<TemplateValidateResponse>('/admin/templates/validate', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // --- Tenant templates (admin) ---

  listTenantTemplates(
    tenantId: string,
    params: TenantTemplateListParams = {},
  ): Promise<TenantReportListResponse> {
    // B-4: GET /admin/tenants/:tenantId/templates — tenant-installed reports (not global catalog)
    return apiFetch<TenantReportListResponse>(
      `/admin/tenants/${tenantId}/templates${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  installTenantTemplate(
    tenantId: string,
    body: InstallTenantTemplateBody,
  ): Promise<TenantReportDetail> {
    // B-1: path must include /install suffix per backend F2.1
    return apiFetch<TenantReportDetail>(`/admin/tenants/${tenantId}/templates/install`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  migrateTenantTemplate(
    tenantId: string,
    tenantReportId: string,
    body: MigrateTenantTemplateBody,
  ): Promise<TenantReportDetail> {
    // B-1: method is POST (not PUT); path is /migrate (not /version) per backend F2.2
    return apiFetch<TenantReportDetail>(
      `/admin/tenants/${tenantId}/templates/${tenantReportId}/migrate`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  uninstallTenantTemplate(tenantId: string, tenantReportId: string): Promise<void> {
    return apiFetch<void>(`/admin/tenants/${tenantId}/templates/${tenantReportId}`, {
      method: 'DELETE',
    });
  },

  toggleEnabled(
    tenantId: string,
    tenantReportId: string,
    body: ToggleEnabledBody,
  ): Promise<TenantReportDetail> {
    // Backend F2.4 uses PUT (not PATCH)
    return apiFetch<TenantReportDetail>(
      `/admin/tenants/${tenantId}/templates/${tenantReportId}/enabled`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    );
  },

  // --- Roles ---

  listRoles(tenantId: string, params: RoleListParams = {}): Promise<RoleListResponse> {
    return apiFetch<RoleListResponse>(
      `/admin/tenants/${tenantId}/roles${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  getRole(tenantId: string, roleId: string): Promise<RoleDetail> {
    return apiFetch<RoleDetail>(`/admin/tenants/${tenantId}/roles/${roleId}`);
  },

  createRole(tenantId: string, body: CreateRoleBody): Promise<RoleDetail> {
    return apiFetch<RoleDetail>(`/admin/tenants/${tenantId}/roles`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateRole(tenantId: string, roleId: string, body: UpdateRoleBody): Promise<RoleDetail> {
    return apiFetch<RoleDetail>(`/admin/tenants/${tenantId}/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteRole(tenantId: string, roleId: string): Promise<void> {
    return apiFetch<void>(`/admin/tenants/${tenantId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  },

  // --- Role members ---

  listRoleMembers(
    tenantId: string,
    roleId: string,
    params: MemberListParams = {},
  ): Promise<MemberListResponse> {
    return apiFetch<MemberListResponse>(
      `/admin/tenants/${tenantId}/roles/${roleId}/members${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  addRoleMember(tenantId: string, roleId: string, body: AddMemberBody): Promise<MemberItem> {
    return apiFetch<MemberItem>(`/admin/tenants/${tenantId}/roles/${roleId}/members`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  removeRoleMember(tenantId: string, roleId: string, memberId: string): Promise<void> {
    return apiFetch<void>(`/admin/tenants/${tenantId}/roles/${roleId}/members/${memberId}`, {
      method: 'DELETE',
    });
  },

  batchRoleMembers(tenantId: string, roleId: string, body: BatchMembersBody): Promise<BatchResult> {
    return apiFetch<BatchResult>(`/admin/tenants/${tenantId}/roles/${roleId}/members/batch`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // --- Role rules ---

  listRoleRules(
    tenantId: string,
    roleId: string,
    params: RuleListParams = {},
  ): Promise<RuleListResponse> {
    return apiFetch<RuleListResponse>(
      `/admin/tenants/${tenantId}/roles/${roleId}/rules${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  createRoleRule(tenantId: string, roleId: string, body: CreateRuleBody): Promise<RuleItem> {
    return apiFetch<RuleItem>(`/admin/tenants/${tenantId}/roles/${roleId}/rules`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  updateRoleRule(
    tenantId: string,
    roleId: string,
    ruleId: string,
    body: UpdateRuleBody,
  ): Promise<RuleItem> {
    return apiFetch<RuleItem>(`/admin/tenants/${tenantId}/roles/${roleId}/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  deleteRoleRule(tenantId: string, roleId: string, ruleId: string): Promise<void> {
    return apiFetch<void>(`/admin/tenants/${tenantId}/roles/${roleId}/rules/${ruleId}`, {
      method: 'DELETE',
    });
  },

  testRoleRule(tenantId: string, body: TestRuleBody): Promise<RuleTestResponse> {
    return apiFetch<RuleTestResponse>(`/admin/tenants/${tenantId}/role-rules/test`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  applyRoleRule(tenantId: string, ruleId: string): Promise<RuleApplyResponse> {
    return apiFetch<RuleApplyResponse>(`/admin/tenants/${tenantId}/role-rules/${ruleId}/apply`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  // --- Grants ---

  listGrants(tenantId: string, params: GrantListParams = {}): Promise<GrantListResponse> {
    return apiFetch<GrantListResponse>(
      `/admin/tenants/${tenantId}/grants${buildQuery(params as Record<string, unknown>)}`,
    );
  },

  upsertGrant(tenantId: string, body: UpsertGrantBody): Promise<GrantItem> {
    return apiFetch<GrantItem>(`/admin/tenants/${tenantId}/grants`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  deleteGrant(tenantId: string, grantId: string): Promise<void> {
    return apiFetch<void>(`/admin/tenants/${tenantId}/grants/${grantId}`, {
      method: 'DELETE',
    });
  },

  applyGrantsMatrix(tenantId: string, body: GrantsMatrixBody): Promise<GrantsMatrixResponse> {
    return apiFetch<GrantsMatrixResponse>(`/admin/tenants/${tenantId}/grants/matrix`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  // --- Audit log ---

  queryAuditLog(tenantId: string, params: AuditLogParams = {}): Promise<AuditLogResponse> {
    return apiFetch<AuditLogResponse>(
      `/admin/tenants/${tenantId}/audit-log${buildQuery(params as Record<string, unknown>)}`,
    );
  },
};
